/* eslint-disable max-len */
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, filter, take, switchMap } from 'rxjs';
import { getLimitOfMaxTimes } from 'src/app/helpers/reminder/reminder';
import { PayloadInterface } from 'src/app/models/interface/payload.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { ReminderStore } from '../../../store/reminder/component-store/reminder.store';

type CreateInstanceReminderData = {
  instanceId?: number;
  userId?: number;
  reminderConfig?: PayloadInterface;
};

@Component({
  selector: 'app-dialog-intervention-instance-reminder-create',
  templateUrl: './dialog-intervention-instance-reminder-create.component.html',
  styleUrls: ['./dialog-intervention-instance-reminder-create.component.scss'],
  providers: [ReminderStore],
})
export class DialogInterventionInstanceReminderCreateComponent implements OnInit, OnDestroy {
  // Reminder form submission
  public reminderForm: UntypedFormGroup;

  public createInterventionInstanceReminderResponse = new BehaviorSubject<string>('DEFAULT');

  public createInterventionInstanceReminderResponse$: Observable<any>;

  public existingReminderConfig: PayloadInterface;

  public userId: number;
  public instanceId: number;

  public languages = ['de', 'en'];
  public selectedLanguage: string;
  public selectedLanguageSubject = new BehaviorSubject<string>('');

  public defaultReminderText: Object;

  public unitList = ['minute', 'hour', 'day'];

  // Ngx datetimepicker
  public showSpinners = false;
  public showSeconds = false;
  public touchUi = false;
  public enableMeridian = false;
  public minDate = new Date();
  public stepHour = 1;
  public stepMinute = 1;
  public stepSecond = 1;
  public color: ThemePalette = 'primary';

  private subscriptions: Subscription[] = [];

  maxTimesErrorMessage = undefined;

  private readonly instanceKey = 'intervention-instance-reminder';
  private readonly greetingKey = 'default_reminder_message_head';
  private readonly textKey = 'default_reminder_message';
  private readonly closingKey = 'default_reminder_closing';
  private readonly signatureKey = 'default_reminder_signature';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CreateInstanceReminderData,
    private dialogRef: MatDialogRef<DialogInterventionInstanceReminderCreateComponent>,
    private helperService: HelperService,
    private httpClient: HttpClient,
    private reminderStore: ReminderStore,
    private translateService: TranslateService,
  ) {
    this.selectedLanguageSubject.next(this.translateService.currentLang);
    this.subscriptions.push(
      this.selectedLanguageSubject
        .pipe(switchMap((language) => this.httpClient.get(`../../../../assets/i18n/${language}.json`)))
        .subscribe((translation) => {
          this.defaultReminderText = translation;
        }),
    );
    this.reminderForm = new UntypedFormGroup({
      starts_at: new UntypedFormControl('', [Validators.required]),
      max_times: new UntypedFormControl('', [Validators.required]),
      time_interval: new UntypedFormControl('', [Validators.required]),
      time_interval_unit: new UntypedFormControl(this.unitList[0]),
      content_greeting: new UntypedFormControl(''),
      content_text: new UntypedFormControl(''),
      content_closing: new UntypedFormControl(''),
      content_signature: new UntypedFormControl(''),
    });
    this.reminderForm.controls['starts_at'].setValue(new Date());

    this.createInterventionInstanceReminderResponse$ = this.reminderStore.createInterventionInstanceReminderResponse$;
  }

  public get f() {
    return this.reminderForm;
  }

  public get helper() {
    return this.helperService;
  }

  get selectedLanguageOnly(): string {
    return this.selectedLanguageSubject.value;
  }

  set selectedLanguageOnly(value: string) {
    this.selectedLanguageSubject.next(value);
  }

  ngOnInit(): void {
    this.instanceId = this.data.instanceId;
    this.userId = this.data.userId;
    this.existingReminderConfig = this.data.reminderConfig;
    if (this.existingReminderConfig) {
      const reminderConfig = this.existingReminderConfig.data.attributes;
      this.f.get('starts_at').patchValue(new Date(this.helper.convertUnixTimestampToDate(reminderConfig.configuration.start)));
      this.f.get('max_times').patchValue(reminderConfig.configuration.max_times || '');
      this.f.get('time_interval').patchValue(reminderConfig.configuration.pause || '');
      this.f.get('time_interval_unit').patchValue(reminderConfig.configuration.time_unit || '');
      this.f.get('content_greeting').patchValue(reminderConfig.content?.greeting || '');
      this.f.get('content_text').patchValue(reminderConfig.content?.text || '');
      this.f.get('content_closing').patchValue(reminderConfig.content?.closing || '');
      this.f.get('content_signature').patchValue(reminderConfig.content?.signature || '');
    }
  }

  public getReminderGreeting(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.greetingKey];
    } else {
      return '';
    }
  }

  public getReminderMessage(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.textKey];
    } else {
      return '';
    }
  }

  public getReminderClosing(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.closingKey];
    } else {
      return '';
    }
  }

  public getReminderSignature(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.signatureKey];
    } else {
      return '';
    }
  }

  getLimitOfMaxTimesOf(unit: 'minute' | 'hour' | 'day') {
    return getLimitOfMaxTimes(unit);
  }

  controlHasErrors(control: AbstractControl): boolean {
    return control.touched && control.invalid;
  }

  resetMaxTimesErrorMessage() {
    this.maxTimesErrorMessage = undefined;
  }

  private getValue<T>(value: T): T | undefined {
    return value || value === '' ? value : undefined;
  }

  public setInterventionInstanceReminder(): void {
    if (this.reminderForm.invalid) {
      return;
    }

    const startsAt = this.helperService.convertStringToUnixTimestamp(
      this.getValue(this.f.get('starts_at').value),
    );
    const maxTimes = this.getValue(this.f.get('max_times').value);
    const timeInterval = this.getValue(this.f.get('time_interval').value);
    const timeIntervalUnit = this.getValue(this.f.get('time_interval_unit').value);
    const contentGreeting = this.f.get('content_greeting').value || this.getReminderGreeting();
    const contentText = this.f.get('content_text').value || this.getReminderMessage();
    const contentClosing = this.f.get('content_closing').value || this.getReminderClosing();
    const contentSignature = this.f.get('content_signature').value || this.getReminderSignature();
    if (!!this.userId && !!this.instanceId) {
      if (this.createInterventionInstanceReminderResponse.value === 'DEFAULT') {
        this.createInterventionInstanceReminderResponse.next('LOADING');

        this.reminderStore.createInterventionInstanceReminder({
          instanceId: this.instanceId,
          receiverId: this.userId,
          maxTimes,
          start: startsAt,
          repeatTime: timeInterval,
          repeatUnit: timeIntervalUnit,
          contentGreeting,
          contentText,
          contentClosing,
          contentSignature,
          locale: this.selectedLanguageSubject.value,
        });

        this.subscriptions.push(
          this.createInterventionInstanceReminderResponse$
            .pipe(
              filter((value) => !!value),
              take(1),
            )
            .subscribe({
              next: (value) => {
                if (value instanceof HttpResponse) {
                  this.createInterventionInstanceReminderResponse.next('SUCCESS');
                  setTimeout(() => {
                    this.createInterventionInstanceReminderResponse.next('DEFAULT');
                    this.dialogRef.close(value);
                  }, 2500);
                } else {
                  if (value.status === 422) {
                    const errors = value.error?.errors[0]?.meta?.errors;
                    const maxTimesKey = "data.attributes.configuration.max_times";
                    if (errors && maxTimesKey in errors) {
                      const maxTimesErrors = errors[maxTimesKey];
                      const defaultError = this.translateService.instant(
                        'intervention-instance-reminder.invalid_max_number_of_repititions',
                      );
                      if (Array.isArray(maxTimesErrors)) {
                        this.maxTimesErrorMessage = maxTimesErrors[0]?.message[0] || defaultError;
                      } else {
                        this.maxTimesErrorMessage = maxTimesErrors || defaultError;
                      }
                    }
                  }
                  this.createInterventionInstanceReminderResponse.next('FAILURE');
                  setTimeout(() => {
                    this.createInterventionInstanceReminderResponse.next('DEFAULT');
                  }, 2500);
                }
              },
            }),
        );
      }
    } else {
      const payload: PayloadInterface = {
        data: {
          type: 'reminders',
          attributes: {
            receiver_id: this.userId || null,
            configuration: {
              max_times: maxTimes,
              start: startsAt,
              pause: timeInterval,
              time_unit: timeIntervalUnit,
            },
            content: {
              greeting: contentGreeting,
              text: contentText,
              closing: contentClosing,
              signature: contentSignature,
              locale: this.selectedLanguageSubject.value,
            },
          },
        },
      };
      this.dialogRef.close(payload);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
